/**
 * delivery moduli uchun umumiy turlar/konstantalar (API + veb + mobil).
 * Faqat delivery moduli egasi tahrirlaydi.
 *
 * Bu yerda faqat SOF (side-effect'siz) funksiyalar: status o'tishi tekshiruvi, naqd/karta
 * taqsimoti, kuryer ish haqi va hisob-kitob formulasi — server, veb va W ilova (kuryer)
 * aynan bir xil qoidani ishlatadi.
 */
import type { L10n } from '../i18n';
import { DELIVERY_STATUSES, type DeliveryStatus, type Tone } from '../statuses';
import { deliveredLineNet, deliveredQtyOf, effectiveExtraDiscount } from './oms';

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

// ------------------------------------------------------------------ Status guruhlari
export const TASK_STATUS_KEYS = Object.keys(DELIVERY_STATUSES) as DeliveryStatus[];

/** Yakuniy holatlar (keyin o'zgarmaydi). */
export const FINAL_TASK_STATUSES: readonly DeliveryStatus[] = TASK_STATUS_KEYS.filter(
  (s) => DELIVERY_STATUSES[s].final,
);
export const isFinalTaskStatus = (s: string): boolean =>
  (FINAL_TASK_STATUSES as readonly string[]).includes(s);

/** Yetkazilgan (pul olingan) holatlar. */
export const DELIVERED_STATUSES: readonly DeliveryStatus[] = ['delivered', 'partial'];
export const isDeliveredStatus = (s: string): boolean =>
  (DELIVERED_STATUSES as readonly string[]).includes(s);

/** Kuryer qo'lidagi (yo'lda yoki natija kutilayotgan) holatlar — keyingi kunga ko'chishi mumkin. */
export const CARRYOVER_STATUSES: readonly DeliveryStatus[] = ['callback', 'rescheduled', 'transfer'];

/**
 * Holat o'zgartirish endpointi orqali qo'yiladigan holatlar (pending/assigned — faqat skan orqali).
 * `partial` («Yetkazildi-Narx o'zgardi») bu yerda YO'Q: u QO'LDA tanlanmaydi — tarkib o'zgartirilib
 * (minus / chegirma) «Yetkazildi» qo'yilganda o'zi hosil bo'ladi (DMS v8 effektiv status 4).
 *
 * «Yo'qolgan» ham YO'Q: DMS 0069 dan beri u STATUS EMAS, BELGI (`tasks.lostAt` + `lostComment`) —
 * buyurtma haqiqiy holatini (kimda, qaysi bosqichda qolgani) ko'rsatib turishi kerak.
 */
export const MANUAL_TASK_STATUSES: readonly DeliveryStatus[] = [
  'on_the_way',
  'delivered',
  'callback',
  'rescheduled',
  'transfer',
  'rejected',
  'canceled',
];

/**
 * Yakuniy SALBIY holatlar (DMS `status.is_final AND polarity = 'negative'`): bekor arizasi belgisi
 * va uning bildirishnomasi aynan shu holatlarda olib tashlanadi (`services/cancelMark.js`).
 */
export const FINAL_NEGATIVE_STATUSES: readonly DeliveryStatus[] = TASK_STATUS_KEYS.filter(
  (s) => DELIVERY_STATUSES[s].final && DELIVERY_STATUSES[s].negative,
);
export const isFinalNegativeStatus = (s: string): boolean =>
  (FINAL_NEGATIVE_STATUSES as readonly string[]).includes(s);

/** Kuryer (W ilova) o'z topshirig'iga qo'yadigan natijalar. */
export const COURIER_TARGET_STATUSES: readonly DeliveryStatus[] = [
  'delivered',
  'callback',
  'rescheduled',
  'transfer',
  'rejected',
  'canceled',
];

/** Kuryer natija qo'ya oladigan joriy holatlar. */
export const COURIER_SOURCE_STATUSES: readonly DeliveryStatus[] = [
  'assigned',
  'on_the_way',
  'callback',
  'rescheduled',
  'transfer',
];

/** Kuryerdan qabul (omborga qaytarish) qilinadigan holatlar. `on_the_way` — avval natija belgilanadi. */
export const RETURNABLE_STATUSES: readonly DeliveryStatus[] = [
  'rejected',
  'canceled',
  'callback',
  'rescheduled',
  'transfer',
];

/**
 * Qabulda `delivery.task.returned_to_warehouse` chiqariladigan holatlar — faqat HAQIQIY qaytarish
 * (rad/bekor). callback/rescheduled/transfer posilkalari omborda keyingi yetkazishni kutadi —
 * wms ularni qaytarish (restock) sifatida qayd qilmasligi kerak.
 */
export const RETURN_EVENT_STATUSES: readonly DeliveryStatus[] = ['rejected', 'canceled'];

// ------------------------------------------------------------------ Sabablar
export const REASON_KINDS = {
  reject: { uz: 'Rad etish', ru: 'Отказ', en: 'Reject', zh: '拒绝', tone: 'danger' },
  callback: { uz: 'Qayta qo‘ng‘iroq', ru: 'Перезвон', en: 'Call back', zh: '需回电', tone: 'warning' },
  transfer: { uz: 'Boshqa kuryerga', ru: 'Передача', en: 'To another courier', zh: '转交其他配送员', tone: 'violet' },
  cancel: { uz: 'Bekor qilish', ru: 'Отмена', en: 'Cancel', zh: '取消', tone: 'danger' },
  reschedule: { uz: 'Boshqa kunga', ru: 'Перенос', en: 'Reschedule', zh: '改期', tone: 'info' },
} as const satisfies Record<string, L10n & { tone: Tone }>;
export type ReasonKind = keyof typeof REASON_KINDS;
export const REASON_KIND_KEYS = Object.keys(REASON_KINDS) as ReasonKind[];

/** Holat → talab qilinadigan sabab turi. */
export const STATUS_REASON_KIND: Partial<Record<DeliveryStatus, ReasonKind>> = {
  rejected: 'reject',
  callback: 'callback',
  transfer: 'transfer',
  canceled: 'cancel',
  rescheduled: 'reschedule',
};

/** Sabab MAJBURIY bo'lgan holatlar (rescheduled uchun sana majburiy, sabab ixtiyoriy). */
export const REASON_REQUIRED_STATUSES: readonly DeliveryStatus[] = [
  'rejected',
  'callback',
  'transfer',
  'canceled',
];

// ------------------------------------------------------------------ Kuryer, hisob-kitob
export const COURIER_STATUSES = {
  active: { uz: 'Faol', ru: 'Активен', en: 'Active', zh: '启用', tone: 'success' },
  inactive: { uz: 'Faol emas', ru: 'Неактивен', en: 'Inactive', zh: '停用', tone: 'neutral' },
} as const satisfies Record<string, L10n & { tone: Tone }>;
export type CourierStatus = keyof typeof COURIER_STATUSES;

export const SETTLEMENT_METHODS = {
  cash: { uz: 'Naqd', ru: 'Наличные', en: 'Cash', zh: '现金' },
  card: { uz: 'Karta (o‘tkazma)', ru: 'Карта (перевод)', en: 'Card (transfer)', zh: '银行卡转账' },
  transfer: { uz: 'Bank o‘tkazmasi', ru: 'Банковский перевод', en: 'Bank transfer', zh: '银行转账' },
} as const satisfies Record<string, L10n>;
export type SettlementMethod = keyof typeof SETTLEMENT_METHODS;

/** Hafta kuni: to'liq nomi L10n, `short` — jadval sarlavhasi uchun qisqartma. */
export interface Weekday extends L10n {
  value: number;
  short: L10n;
}

export const WEEKDAYS: readonly Weekday[] = [
  { value: 1, uz: 'Dushanba', ru: 'Понедельник', en: 'Monday', zh: '星期一', short: { uz: 'Du', ru: 'Пн', en: 'Mon', zh: '一' } },
  { value: 2, uz: 'Seshanba', ru: 'Вторник', en: 'Tuesday', zh: '星期二', short: { uz: 'Se', ru: 'Вт', en: 'Tue', zh: '二' } },
  { value: 3, uz: 'Chorshanba', ru: 'Среда', en: 'Wednesday', zh: '星期三', short: { uz: 'Ch', ru: 'Ср', en: 'Wed', zh: '三' } },
  { value: 4, uz: 'Payshanba', ru: 'Четверг', en: 'Thursday', zh: '星期四', short: { uz: 'Pa', ru: 'Чт', en: 'Thu', zh: '四' } },
  { value: 5, uz: 'Juma', ru: 'Пятница', en: 'Friday', zh: '星期五', short: { uz: 'Ju', ru: 'Пт', en: 'Fri', zh: '五' } },
  { value: 6, uz: 'Shanba', ru: 'Суббота', en: 'Saturday', zh: '星期六', short: { uz: 'Sh', ru: 'Сб', en: 'Sat', zh: '六' } },
  { value: 7, uz: 'Yakshanba', ru: 'Воскресенье', en: 'Sunday', zh: '星期日', short: { uz: 'Ya', ru: 'Вс', en: 'Sun', zh: '日' } },
];

/** "YYYY-MM-DD" → ISO hafta kuni (1 = dushanba ... 7 = yakshanba). */
export function isoWeekday(day: string): number {
  const d = new Date(`${day}T00:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}

// ------------------------------------------------------------------ Skan natijalari
export const SCAN_RESULTS = {
  ok: { uz: 'Biriktirildi', ru: 'Назначен', en: 'Assigned', zh: '已分配', tone: 'success' },
  already: { uz: 'Allaqachon shu kunda', ru: 'Уже на этот день', en: 'Already on this day', zh: '已在该日', tone: 'warning' },
  not_found: { uz: 'Topshiriq topilmadi', ru: 'Задание не найдено', en: 'Task not found', zh: '未找到任务', tone: 'danger' },
  final: { uz: 'Topshiriq yakunlangan', ru: 'Задание завершено', en: 'The task is already finished', zh: '任务已完成', tone: 'danger' },
  other_courier: { uz: 'Boshqa kuryerda', ru: 'У другого курьера', en: 'Assigned to another courier', zh: '在其他配送员名下', tone: 'danger' },
  external: { uz: 'Tashqi kuryer xizmati hududi', ru: 'Зона внешней службы доставки', en: 'Third-party carrier zone', zh: '第三方快递区域', tone: 'danger' },
  locked: { uz: 'Kun biriktirilgan (skan yopiq)', ru: 'День назначен (скан закрыт)', en: 'The day is assigned (scanning closed)', zh: '当日已分配（扫码已关闭）', tone: 'danger' },
  checked: { uz: 'Qabul qilindi', ru: 'Принято', en: 'Accepted', zh: '已接收', tone: 'success' },
  not_returnable: { uz: 'Qabul qilib bo‘lmaydi', ru: 'Нельзя принять', en: 'Cannot be accepted', zh: '无法接收', tone: 'danger' },
  cancel_requested: { uz: 'Bekor arizasi bor — skan qilinmaydi', ru: 'Есть заявка на отмену — скан запрещён', en: 'Cancellation requested — scanning blocked', zh: '已申请取消 — 禁止扫码', tone: 'danger' },
} as const satisfies Record<string, L10n & { tone: Tone }>;
export type ScanResult = keyof typeof SCAN_RESULTS;

/**
 * SKAN QOIDALARI — SOF qaror (`delivery/days.ts` `scan()` bazadan o'qiydi, qarorni shu yerda oladi).
 * Posilkani ikki joydan skanerlash mumkin va IKKALASI HAM shu zanjirdan o'tadi:
 * logist kun kartochkasidan (`POST /delivery/days/:id/scan`) va kuryer o'z telefonidan
 * (`POST /delivery/me/day/scan`) — kim birinchi qilsa, o'sha. Qoida bitta joyda yoziladi.
 */
export interface ScanTaskState {
  /** O'z kuryer xizmatimizmi (tashqi carrier emas). */
  ownCarrier: boolean;
  /** Bekor arizasi belgisi. */
  cancelRequested: boolean;
  /** Topshiriq bog'langan kuryer kuni (bo'lmasa — null). */
  courierDayId: string | null;
  /** Topshiriq holati (`delivery` status kaliti). */
  status: string;
}

/**
 * Kun skanga ochiqmi. `open` bo'lmasa — `locked`. Kun umuman topilmagani ham `locked`:
 * kuryer o'zi skanerlaganda bugunga kuni hali ochilmagan bo'lishi mumkin (xabar farq qiladi).
 */
export function scanDayBlock(day: { status: string } | null | undefined): ScanResult | null {
  return day && day.status === 'open' ? null : 'locked';
}

/**
 * `other_courier` ni tekshirish uchun qaysi kuryer kunini o'qish kerak (kerak bo'lmasa — null).
 * Faqat kuryer QO'LIDAGI topshiriq boshqa kunda ushlanib qoladi; callback/rescheduled/transfer
 * esa yangi urinish sifatida bemalol ko'chiriladi (attempt + 1).
 */
export function scanOtherDayId(t: ScanTaskState, dayId: string): string | null {
  if (!t.courierDayId || t.courierDayId === dayId) return null;
  return t.status === 'assigned' || t.status === 'on_the_way' ? t.courierDayId : null;
}

/**
 * Topshiriqni shu kunga biriktirishga TO'SIQ (yo'q bo'lsa — null). Tartib muhim:
 * tashqi xizmat → bekor arizasi → allaqachon shu kunda → boshqa kuryerda.
 *
 * BEKOR ARIZASI KELGAN TOPSHIRIQ SKAN QILINMAYDI (DMS 2026-09-07 talabi, `assignScan.js`).
 * Ariza — «tovarni mijozga BERMANG» degani. Avval topshiriq odatdagidek skan qilinib kuryerga
 * ketardi va ogohlantirish faqat ilovada chiqardi — ya'ni tovar allaqachon yo'lda bo'lardi.
 * Endi skanning O'ZI to'siladi. Xatoni tuzatish yo'li: operator arizani «Yopish» bilan hal
 * qiladi (`POST /delivery/tasks/:id/cancel-request/resolve`) yoki topshiriqni bekor qiladi.
 *
 * `otherDayStatus` — `scanOtherDayId()` ko'rsatgan kunning holati (o'qilmagan bo'lsa null).
 * Yopilgan kundagi topshiriq to'sib qo'yilmaydi — u yangi kunga o'tadi.
 */
export function scanTaskBlock(
  t: ScanTaskState,
  dayId: string,
  otherDayStatus: string | null,
): ScanResult | null {
  if (!t.ownCarrier) return 'external';
  if (t.cancelRequested) return 'cancel_requested';
  if (t.courierDayId === dayId) return 'already';
  if (scanOtherDayId(t, dayId) && otherDayStatus && otherDayStatus !== 'closed') return 'other_courier';
  return null;
}

// ------------------------------------------------------------------ Bekor arizasi (DMS v8 cancel_request)
/**
 * Bekor arizasining manbasi (DMS `cancel_request.source`): mijoz botidan kelgan so'rov, operator
 * qo'lda kiritgan ariza yoki tashqi tizim (Stocker/marketplace) xabari.
 */
export const CANCEL_REQUEST_SOURCES = {
  support: { uz: 'Mijoz so‘rovi (bot)', ru: 'Запрос клиента (бот)', en: 'Customer request (bot)', zh: '客户申请（机器人）' },
  manual: { uz: 'Operator kiritdi', ru: 'Добавлено оператором', en: 'Added by operator', zh: '操作员添加' },
  external: { uz: 'Tashqi tizim', ru: 'Внешняя система', en: 'External system', zh: '外部系统' },
} as const satisfies Record<string, L10n>;
export type CancelRequestSource = keyof typeof CANCEL_REQUEST_SOURCES;
export const CANCEL_REQUEST_SOURCE_KEYS = Object.keys(CANCEL_REQUEST_SOURCES) as CancelRequestSource[];

// ------------------------------------------------------------------ Refund arizasi (DMS v8 refund_request)
/** Refund arizasi uchun ruxsat etilgan ASL topshiriq holatlari (DMS `ALLOWED_REFUND_STATUSES = [2, 4]`). */
export const REFUNDABLE_STATUSES: readonly DeliveryStatus[] = ['delivered', 'partial'];

/**
 * Yetkazish haqini kim to'laydi (DMS `refund_request.delivery_payer`):
 *  - `client` — MIJOZ to'laydi: refundga yetkazish qatori KIRMAYDI (faqat tovar puli qaytadi);
 *  - `none`   — to'lamaydi: yetkazish qatori asl narxi bilan (MUSBAT) refundga QO'SHILADI.
 */
export const REFUND_DELIVERY_PAYERS = {
  client: { uz: 'Mijoz to‘laydi', ru: 'Платит клиент', en: 'Customer pays', zh: '由客户承担' },
  none: { uz: 'To‘lamaydi', ru: 'Не платит', en: 'Customer does not pay', zh: '客户不承担' },
} as const satisfies Record<string, L10n>;
export type RefundDeliveryPayer = keyof typeof REFUND_DELIVERY_PAYERS;
export const REFUND_DELIVERY_PAYER_KEYS = Object.keys(REFUND_DELIVERY_PAYERS) as RefundDeliveryPayer[];

/** Topshiriq turi: oddiy yetkazish yoki refund (DMS `orders.order_type` = `order` | `refund`). */
export const TASK_KINDS = {
  order: { uz: 'Buyurtma', ru: 'Заказ', en: 'Order', zh: '订单' },
  refund: { uz: 'Refund', ru: 'Возврат', en: 'Refund', zh: '退款' },
} as const satisfies Record<string, L10n>;
export type TaskKind = keyof typeof TASK_KINDS;

/** Refund topshirig'i raqami (DMS `RF-<order_id>`); takrorlansa `-2`, `-3` … qo'shiladi. */
export const refundNumber = (orderNumber: string, n = 1): string =>
  n > 1 ? `RF-${orderNumber}-${n}` : `RF-${orderNumber}`;

/**
 * Refund summasi (DMS `computeAmount`): tanlangan tovarlar + «To'lamaydi» bo'lsa yetkazish haqi.
 * Tovar narxi — mijoz TO'LAGAN narx (buyurtma chegirmasi ayrilgan qator narxi).
 */
export function refundAmountOf(
  items: readonly { qty: number; price: number; discount?: number }[],
  deliveryFee: number,
  payer: RefundDeliveryPayer,
): number {
  let total = 0;
  for (const it of items) total += Math.max(0, it.qty) * it.price - (it.discount ?? 0);
  if (payer === 'none') total += Math.max(0, deliveryFee || 0);
  return round2(Math.max(0, total));
}

// ------------------------------------------------------------------ Topshiriq tarkibi
/**
 * Topshiriq tarkibi — buyurtma qatorlarining nusxasi (DMS v8 `order_detail`).
 *
 * Kuryer/operator eshik oldida tarkibni o'zgartiradi (DMS dagi «minus» va «chegirma»):
 *  - `deliveredQty` — SOTILGAN miqdor (DMS `solded_quantity`); `null` = to'liq (qty);
 *  - `extraDiscount` — yetkazishda berilgan chegirma, qator bo'yicha JAMI so'm (DMS `discount`).
 *    MANFIY bo'lsa — ANTICHEGIRMA (ustama, summa oshadi). Musbat chegirma sotilgan tovar
 *    summasidan oshmaydi; sotilmagan qatorda ikkalasi ham hisoblanmaydi (`effectiveExtraDiscount`).
 * Biror qator kamaysa yoki chegirma/antichegirma bo'lsa «Yetkazildi» o'zi «Yetkazildi-Narx
 * o'zgardi» (`partial`) bo'ladi — holat qo'lda tanlanmaydi (`isPriceChanged`).
 */
export interface TaskItem {
  /** oms buyurtma qatori ID si (eski topshiriqlarda yo'q — SKU bo'yicha moslanadi). */
  itemId?: string | null;
  skuId: string | null;
  title: string;
  qty: number;
  price: number;
  /** Buyurtmadagi qator chegirmasi, jami so'm (oms). */
  discount: number;
  deliveredQty: number | null;
  extraDiscount?: number | null;
  discountComment?: string | null;
}

/** Qisman yetkazishda olinadigan summa: yetkazilgan ulush (chegirma proporsional) + yetkazish haqi. */
export function partialAmount(
  items: readonly TaskItem[],
  delivered: ReadonlyMap<string, number>,
  deliveryFee: number,
): number {
  let sum = 0;
  items.forEach((it, i) => {
    const q = delivered.get(itemKey(it, i)) ?? 0;
    if (q <= 0 || it.qty <= 0) return;
    sum += q * it.price - (it.discount * q) / it.qty;
  });
  return round2(sum + (sum > 0 ? deliveryFee : 0));
}

/** Qator kaliti (tartib raqami) — bir SKU bir necha qatorda bo'lishi mumkin. */
export const itemKey = (_it: Pick<TaskItem, 'skuId'>, index: number): string => `#${index}`;
/** Qatorning tovar identifikatori (deliveredItems.skuId): skuId yoki (SKU siz qator uchun) qator kaliti. */
export const itemSku = (it: Pick<TaskItem, 'skuId'>, index: number): string => it.skuId ?? itemKey(it, index);

/**
 * deliveredItems ([{skuId, qty}]) → qator bo'yicha miqdorlar. Bir xil SKU bir necha qatorda bo'lsa —
 * birinchi qatorlardan boshlab taqsimlanadi.
 */
export function distributeDeliveredItems(
  items: readonly TaskItem[],
  delivered: readonly { skuId: string; qty: number }[],
): Map<string, number> {
  const want = new Map<string, number>();
  for (const d of delivered) want.set(d.skuId, (want.get(d.skuId) ?? 0) + Math.max(0, d.qty));
  const out = new Map<string, number>();
  items.forEach((it, i) => {
    const key = itemKey(it, i);
    const sku = itemSku(it, i);
    const left = want.get(sku) ?? 0;
    const q = Math.min(left, it.qty);
    out.set(key, q);
    want.set(sku, left - q);
  });
  return out;
}

// ------------------------------------------------------------------ Tarkib: minus / chegirma (DMS v8)
/** Qatorning sotilgan miqdori (0..qty). */
export const itemSoldQty = (it: Pick<TaskItem, 'qty' | 'deliveredQty'>): number => deliveredQtyOf(it);

/** Qatorga beriladigan chegirma chegarasi — sotilgan tovar sof summasi (0 — tovar to'liq qaytgan). */
export function maxExtraDiscount(it: TaskItem): number {
  if (deliveredQtyOf(it) <= 0) return 0;
  return Math.max(0, deliveredLineNet({ ...it, extraDiscount: 0 }));
}

/** Qatorga hisobga olinadigan yetkazish chegirmasi (manfiy — antichegirma). */
export function itemExtraDiscount(it: TaskItem): number {
  const soldNet = deliveredLineNet({ ...it, extraDiscount: 0 });
  return effectiveExtraDiscount(soldNet, deliveredQtyOf(it), it.extraDiscount);
}

/** Narx o'zgarganmi (DMS: o'zgargan summa ≠ asl YOKI qaytgan tovar bor) — «Yetkazildi-Narx o'zgardi». */
export function isPriceChanged(items: readonly TaskItem[]): boolean {
  return items.some((it) => deliveredQtyOf(it) < it.qty || itemExtraDiscount(it) !== 0);
}

export interface TaskAmounts {
  /** Buyurtmaning asl summasi: tovarlar (buyurtma chegirmasi bilan) + yetkazish. */
  original: number;
  /** O'zgargan summa (DMS `changed_amount`): sotilgan tovarlar sof + yetkazish (biror narsa sotilgan bo'lsa). */
  changed: number;
  /**
   * Mijozdan olinadigan: COD − (asl − o'zgargan). Oldindan to'langan buyurtmada (COD 0) faqat
   * antichegirma (ustama) olinadi; summa kamaysa mijozga qaytarish moliya modulida.
   */
  collect: number;
  soldQty: number;
  returnedQty: number;
  /** Qaytgan tovarlar summasi (buyurtma narxida). */
  returnedAmount: number;
  /** Jami yetkazish chegirmasi (manfiy — ustama). */
  extraDiscount: number;
  priceChanged: boolean;
}

export function taskAmounts(t: {
  items: readonly TaskItem[];
  codAmount: number;
  deliveryFee: number;
}): TaskAmounts {
  let original = 0;
  let soldNet = 0;
  let soldNetBefore = 0;
  let soldQty = 0;
  let returnedQty = 0;
  let extraDiscount = 0;
  for (const it of t.items) {
    const dq = deliveredQtyOf(it);
    original += deliveredLineNet({ ...it, deliveredQty: it.qty, extraDiscount: 0 });
    const before = deliveredLineNet({ ...it, extraDiscount: 0 });
    soldNetBefore += before;
    soldNet += deliveredLineNet(it);
    extraDiscount += itemExtraDiscount(it);
    soldQty += dq;
    returnedQty += it.qty - dq;
  }
  const fee = Math.max(0, t.deliveryFee || 0);
  original = round2(original + fee);
  const changed = round2(soldNet + (soldQty > 0 ? fee : 0));
  return {
    original,
    changed,
    collect: Math.max(0, round2(t.codAmount + changed - original)),
    soldQty,
    returnedQty,
    returnedAmount: round2(original - fee - soldNetBefore),
    extraDiscount: round2(extraDiscount),
    priceChanged: isPriceChanged(t.items),
  };
}

export type ItemEditAction = 'minus' | 'discount' | 'update';

export interface ItemEditInput {
  /** `update`: sotilgan miqdor (0..qty). */
  soldQty?: number | null;
  /** `discount` / `update`: qator bo'yicha jami chegirma; manfiy — antichegirma; null — olib tashlash. */
  discount?: number | null;
  comment?: string | null;
}

/**
 * Qator amali (DMS v8 `DETAIL_ACTIONS`):
 *  - minus — qatorni to'liq qaytarilgan (sotilgan 0) ↔ to'liq sotilgan holatiga almashtiradi;
 *    XOM chegirma TEGILMAYDI (sotilmagan qatorda u baribir hisoblanmaydi);
 *  - discount — chegirma (+ izoh), sotilgan summa bilan cheklanadi; manfiy — antichegirma, cheklanmaydi;
 *  - update — sotilgan miqdor + chegirma + izoh birga (chegirma YANGI miqdorga qarab cheklanadi).
 */
export function applyItemEdit(it: TaskItem, action: ItemEditAction, input: ItemEditInput = {}): TaskItem {
  const next: TaskItem = { ...it };
  const capDiscount = (raw: number | null | undefined, target: TaskItem): number | null => {
    if (raw === null || raw === undefined || Number.isNaN(Number(raw))) return null;
    const v = round2(Number(raw));
    if (deliveredQtyOf(target) <= 0) return 0;
    if (v < 0) return v;
    return Math.min(v, maxExtraDiscount({ ...target, extraDiscount: 0 }));
  };
  if (action === 'minus') {
    next.deliveredQty = it.qty > 0 && deliveredQtyOf(it) === 0 ? null : 0;
    return next;
  }
  if (action === 'update' && input.soldQty !== undefined && input.soldQty !== null) {
    const s = Math.round(Number(input.soldQty));
    const sold = Math.max(0, Math.min(it.qty, Number.isNaN(s) ? it.qty : s));
    next.deliveredQty = sold === it.qty ? null : sold;
  }
  if (input.discount !== undefined) next.extraDiscount = capDiscount(input.discount, next);
  if (input.comment !== undefined) next.discountComment = input.comment?.trim() || null;
  return next;
}

/** Hodisa uchun qatorlar: buyurtma tartibida, sotilgan miqdor va yetkazish chegirmasi bilan. */
export function eventItems(items: readonly TaskItem[]) {
  return items.map((it) => ({
    itemId: it.itemId ?? null,
    skuId: it.skuId,
    qty: it.qty,
    deliveredQty: deliveredQtyOf(it),
    extraDiscount: itemExtraDiscount(it),
  }));
}

// ------------------------------------------------------------------ Holat o'zgartirish tekshiruvi
export type StatusActor = 'courier' | 'logist';

export interface StatusChangeInput {
  status: DeliveryStatus;
  cashAmount?: number | null;
  cardAmount?: number | null;
  paymentSystemCode?: string | null;
  /** Karta to'lovining CHEK RASMI (DMS `orders.receipt`) — karta puli bo'lsa MAJBURIY. */
  receiptPhotoUrl?: string | null;
  reasonId?: string | null;
  rescheduleDate?: string | null;
}

export interface StatusTaskState {
  status: DeliveryStatus;
  codAmount: number;
  deliveryFee: number;
  items: readonly TaskItem[];
  /** Topshiriqda ALLAQACHON saqlangan chek rasmi (yangisi yuborilmasa shu hisobga olinadi). */
  receiptPhotoUrl?: string | null;
}

export type StatusChangeResult =
  | {
      ok: true;
      cashAmount: number;
      cardAmount: number;
      /** Mijozdan olingan jami (naqd + karta). */
      total: number;
      /** Yangilangan tarkib (deliveredQty bilan). */
      items: TaskItem[];
      partial: boolean;
    }
  | { ok: false; field?: string; error: string };

const fail = (error: string, field?: string): StatusChangeResult => ({ ok: false, error, field });

/**
 * Holat o'zgartirishni tekshirish (DMS v8 qoidalari umumlashtirilgan):
 *  - yakuniy holatdan o'zgartirib bo'lmaydi; pending/assigned faqat skan orqali;
 *  - kuryer faqat COURIER_TARGET_STATUSES ni, faqat COURIER_SOURCE_STATUSES dan qo'yadi;
 *  - `partial` QO'LDA tanlanmaydi: «Yetkazildi» qo'yilganda tarkib o'zgargan bo'lsa (minus /
 *    chegirma — `applyItemEdit`) natija o'zi «Yetkazildi-Narx o'zgardi» bo'ladi (`isPriceChanged`);
 *  - yetkazishda jami = `taskAmounts().collect` (o'zgargan summa); NAQD QO'LDA YOZILMAYDI —
 *    `naqd = jami − karta` (DMS 2026-09-08), karta jamidan oshmaydi; karta > 0 — to'lov tizimi
 *    VA CHEK RASMI majburiy (`cardRuleError`, DMS 2026-09-08 — web, mobil va PWA uchun bir xil);
 *    hech narsa sotilmagan bo'lsa — «Rad etildi» tanlanadi;
 *  - rejected/callback/transfer/canceled: mos turdagi sabab majburiy;
 *  - rescheduled: yangi sana (bugundan oldin emas) majburiy.
 *
 * `reasonKind` — tanlangan sababning turi (servis bazadan topadi; topilmasa null).
 */
export function validateStatusChange(
  task: StatusTaskState,
  input: StatusChangeInput,
  opts: { actor: StatusActor; today: string; reasonKind?: ReasonKind | null },
): StatusChangeResult {
  const to = input.status;
  if (!(to in DELIVERY_STATUSES)) return fail('Noma’lum holat', 'status');
  if (isFinalTaskStatus(task.status))
    return fail('Topshiriq yakunlangan — holatni o‘zgartirib bo‘lmaydi', 'status');
  if (to === 'partial')
    return fail('“Yetkazildi-Narx o‘zgardi” qo‘lda tanlanmaydi — tarkibni o‘zgartirib “Yetkazildi” ni tanlang', 'status');
  if (!MANUAL_TASK_STATUSES.includes(to)) return fail('Bu holat faqat skan orqali qo‘yiladi', 'status');
  if (to === task.status && to !== 'callback' && to !== 'rescheduled')
    return fail('Topshiriq allaqachon shu holatda', 'status');
  if (opts.actor === 'courier') {
    if (!COURIER_TARGET_STATUSES.includes(to)) return fail('Kuryer bu holatni qo‘ya olmaydi', 'status');
    if (!COURIER_SOURCE_STATUSES.includes(task.status))
      return fail('Topshiriq hali kuryerga biriktirilmagan', 'status');
  } else if (to !== 'canceled' && task.status === 'pending') {
    return fail('Avval topshiriqni kuryer kuniga biriktiring (skan)', 'status');
  }

  // --- sabab
  const kind = STATUS_REASON_KIND[to];
  if (REASON_REQUIRED_STATUSES.includes(to)) {
    if (!input.reasonId) return fail('Sababni tanlang', 'reasonId');
    if (opts.reasonKind !== kind) return fail('Sabab bu holatga mos emas', 'reasonId');
  } else if (input.reasonId && kind && opts.reasonKind && opts.reasonKind !== kind) {
    return fail('Sabab bu holatga mos emas', 'reasonId');
  }

  // --- sana
  if (to === 'rescheduled') {
    const d = input.rescheduleDate ?? '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return fail('Yangi yetkazish sanasini kiriting', 'rescheduleDate');
    if (d < opts.today) return fail('Sana bugundan oldin bo‘lishi mumkin emas', 'rescheduleDate');
  }

  const items = task.items.map((i) => ({ ...i }));
  if (to !== 'delivered') {
    return { ok: true, cashAmount: 0, cardAmount: 0, total: 0, items, partial: false };
  }

  // --- tarkib (minus / chegirma oldindan `applyItemEdit` bilan yozilgan)
  const a = taskAmounts({ items, codAmount: task.codAmount, deliveryFee: task.deliveryFee });
  if (a.soldQty <= 0) return fail('Hech narsa sotilmagan — “Rad etildi” holatini tanlang', 'items');
  const total = a.collect;
  const pay = splitPayment(total, input, task);
  if (!pay.ok) return pay;
  return { ok: true, cashAmount: pay.cash, cardAmount: pay.card, total, items, partial: a.priceChanged };
}

/**
 * **KARTA TO'LOVIDA TO'LOV TIZIMI VA CHEK RASMI MAJBURIY** (DMS 2026-09-08, `cardRuleError`).
 *
 * Karta puli kassa bilan CHEK orqali solishtiriladi, shuning uchun qoida serverda ham, vebda ham,
 * kuryer ilovasida ham AYNI bitta joydan keladi: karta summasi 0 dan katta bo'lsa to'lov tizimi
 * TANLANGAN va chek rasmi BIRIKTIRILGAN bo'lishi shart. Chek shu saqlashda yuborilmasa,
 * topshiriqda allaqachon saqlangani (`saved.receiptPhotoUrl`) hisobga olinadi.
 */
export function cardRuleError(
  card: number,
  input: Pick<StatusChangeInput, 'paymentSystemCode' | 'receiptPhotoUrl'>,
  saved?: Pick<StatusTaskState, 'receiptPhotoUrl'> | null,
): { field: string; error: string } | null {
  if (card <= 0) return null;
  if (!input.paymentSystemCode?.trim())
    return { field: 'paymentSystemCode', error: 'Karta to‘lovi uchun to‘lov tizimini tanlang' };
  const receipt = input.receiptPhotoUrl === undefined ? saved?.receiptPhotoUrl : input.receiptPhotoUrl;
  if (!receipt?.trim())
    return { field: 'receiptPhotoUrl', error: 'Karta to‘lovi uchun chek rasmini biriktiring' };
  return null;
}

/**
 * Naqd / karta taqsimoti (DMS `applyMoneyRules`): karta kiritiladi, naqd — qoldiq (`jami − karta`).
 * Faqat naqd berilsa — karta qoldiq. Ikkalasi berilsa — yig'indi jamiga teng bo'lishi shart.
 * Karta puli bo'lsa oxirida `cardRuleError` (to'lov tizimi + chek rasmi) tekshiriladi.
 */
export function splitPayment(
  total: number,
  input: Pick<StatusChangeInput, 'cashAmount' | 'cardAmount' | 'paymentSystemCode' | 'receiptPhotoUrl'>,
  saved?: Pick<StatusTaskState, 'receiptPhotoUrl'> | null,
): { ok: true; cash: number; card: number } | { ok: false; field?: string; error: string } {
  const cashIn = input.cashAmount ?? null;
  const cardIn = input.cardAmount ?? null;
  if ((cashIn !== null && cashIn < 0) || (cardIn !== null && cardIn < 0))
    return { ok: false, error: 'Summa manfiy bo‘lishi mumkin emas', field: 'cashAmount' };
  let cash: number;
  let card: number;
  if (cashIn === null && cardIn === null) {
    cash = total;
    card = 0;
  } else if (cashIn === null) {
    card = round2(cardIn!);
    if (card > total) return { ok: false, error: 'Karta summasi jami summadan oshmasligi kerak', field: 'cardAmount' };
    cash = round2(total - card);
  } else if (cardIn === null) {
    cash = round2(cashIn);
    if (cash > total) return { ok: false, error: 'Naqd summa jami summadan oshmasligi kerak', field: 'cashAmount' };
    card = round2(total - cash);
  } else {
    cash = round2(cashIn);
    card = round2(cardIn);
    if (Math.abs(cash + card - total) > 1)
      return { ok: false, error: `Naqd + karta jami summaga (${total}) teng bo‘lishi kerak`, field: 'cashAmount' };
  }
  const cardErr = cardRuleError(card, input, saved);
  if (cardErr) return { ok: false, ...cardErr };
  return { ok: true, cash, card };
}

// ------------------------------------------------------------------ Kuryer ish haqi
/**
 * Stavka qoidasi — DMS v8 dagi qattiq yozilgan "40k/50k yetkazish turi" chegirmalarining
 * sozlanadigan o'rnini bosuvchisi. Birinchi mos kelgan qoida qo'llanadi:
 *   - `total` — topshiriq uchun JAMI stavka shu summaga almashtiriladi (DMS: 40 000 / 50 000);
 *   - `add`   — bazaviy stavkaga qo'shiladi (manfiy bo'lishi mumkin).
 * Farq (jami − bazaviy) NAQD qismdan olinadi/qo'shiladi (DMS formulasi kabi), karta qismi o'zgarmaydi.
 */
export interface RateRule {
  code: string;
  label: string;
  when?: {
    zones?: string[];
    paymentType?: 'cash' | 'card' | 'mixed';
    partial?: boolean;
    deliveryFee?: number;
    minAmount?: number;
  };
  total?: number;
  add?: number;
}

export interface CourierRate {
  /** Har yetkazilgan topshiriq uchun — kuryer kassadan (yig'ilgan naqddan) oladigan qism. */
  cashRate: number;
  /** Har yetkazilgan topshiriq uchun — kuryer kartasiga o'tkaziladigan qism (naqddan ushlanmaydi). */
  cardRate: number;
  rules: readonly RateRule[];
}

export interface SalaryTask {
  zoneCode: string | null;
  cashAmount: number;
  cardAmount: number;
  deliveryFee: number;
  partial: boolean;
}

export const paymentTypeOf = (t: Pick<SalaryTask, 'cashAmount' | 'cardAmount'>): 'cash' | 'card' | 'mixed' =>
  t.cardAmount > 0 && t.cashAmount > 0 ? 'mixed' : t.cardAmount > 0 ? 'card' : 'cash';

export function ruleMatches(rule: RateRule, t: SalaryTask): boolean {
  const w = rule.when ?? {};
  if (w.zones?.length && !(t.zoneCode && w.zones.includes(t.zoneCode))) return false;
  if (w.paymentType && paymentTypeOf(t) !== w.paymentType) return false;
  if (w.partial !== undefined && w.partial !== t.partial) return false;
  if (w.deliveryFee !== undefined && Math.abs(t.deliveryFee - w.deliveryFee) > 0.01) return false;
  if (w.minAmount !== undefined && t.cashAmount + t.cardAmount < w.minAmount) return false;
  return true;
}

/** Bitta yetkazilgan topshiriq uchun ish haqi: { naqd qism, karta qism, jami, qoida }. */
export function taskSalary(
  rate: CourierRate,
  t: SalaryTask,
): { cash: number; card: number; total: number; rule: RateRule | null } {
  const base = rate.cashRate + rate.cardRate;
  const rule = rate.rules.find((r) => ruleMatches(r, t)) ?? null;
  const total = Math.max(0, rule?.total ?? base + (rule?.add ?? 0));
  const card = Math.min(rate.cardRate, total);
  return { cash: round2(total - card), card: round2(card), total: round2(total), rule };
}

// ------------------------------------------------------------------ Hisob-kitob
export interface SettlementTask extends SalaryTask {
  status: DeliveryStatus;
  refundAmount: number;
  checked: boolean;
  /** `refund` — RF- topshirig'i (DMS `order_type='refund'`): ish haqi va yig'ilgan pulga KIRMAYDI. */
  kind?: TaskKind;
}

export interface SettlementInput {
  paidCash: number;
  paidCard: number;
}

/**
 * Hisob-kitob REJIMI — DMS v8 dagi IKKI RAQAM (audit §1.2.A.1):
 *
 *  - `official` (**kassir**, DMS `assign_view`): kuryerdan QABUL QILINMAGAN «Yetkazildi-Narx
 *    o'zgardi» va qabul qilinmagan refund rasmiy summaga KIRMAYDI — kassir summani operator
 *    «Kuryerdan qabul» da tasdiqlagandan keyin hisoblaydi. Kun yopilganda qolgan yetkazilganlar
 *    qabul qilingan deb belgilanadi, ya'ni yopilish payti ikki raqam tenglashadi;
 *  - `estimate` (**kuryer**, DMS `services/courierEstimate.js`): AYNI formulalar `checked` sharti
 *    OLIB TASHLANGAN holda — kuryer kun davomida qo'lidagi pulni ko'rib turadi. Bu raqam
 *    kassirning raqamini O'ZGARTIRMAYDI.
 *
 * `pending*` maydonlari — ikki raqam orasidagi farq (rasmiy hisobga hali kirmaganlar).
 */
export type SettlementMode = 'official' | 'estimate';

export interface SettlementSummary {
  mode: SettlementMode;
  totalTasks: number;
  deliveredCount: number;
  partialCount: number;
  /** Yetkazilmagan va kuryerdan qabul qilingan. */
  returnedCount: number;
  /** Yetkazilmagan, hali qabul qilinmagan (yopishdan oldin qabul qilinishi shart). */
  mustReturnCount: number;
  deliveredAmount: number;
  cashCollected: number;
  cardCollected: number;
  refunds: number;
  salaryCash: number;
  salaryCard: number;
  salary: number;
  /** Kuryer topshirishi kerak bo'lgan naqd: naqd − qaytarilgan − ish haqining naqd qismi. */
  expectedCash: number;
  paidCash: number;
  paidCard: number;
  /** Qoldiq: > 0 — kuryer qarzdor, < 0 — ortiqcha topshirilgan. */
  remained: number;
  byRule: { code: string; label: string; count: number; amount: number }[];
  /** RASMIY hisobga hali kirmagan «Narx o'zgardi» topshiriqlari soni (DMS `pending_delivered`). */
  pendingDelivered: number;
  /** Rasmiy hisobga hali kirmagan refund (qabul qilinmagan) soni (DMS `pending_refund`). */
  pendingRefund: number;
  /** Rasmiy hisobga kirmagan yetkazishlarda yig'ilgan pul (naqd + karta) — DMS `pending_amount`. */
  pendingAmount: number;
}

/**
 * Kuryer kuni hisob-kitobi (DMS v8 `assign_view` formulasining umumlashtirilgani):
 *
 *   salaryCash = Σ (yetkazilgan topshiriq) taskSalary.cash   ← DMS: count × cash_rate − (rate − 40k) × n40k − ...
 *   salaryCard = Σ (yetkazilgan topshiriq) taskSalary.card   ← kartaga o'tkaziladi, naqddan ushlanmaydi
 *   expectedCash = (naqd yig'ilgan − mijozga qaytarilgan) − salaryCash
 *   remained     = expectedCash − paidCash − paidCard          ← DMS: remained_amount
 *
 * `paidCard` — kuryer naqdni kassaga emas, karta/o'tkazma orqali topshirgan qism (DMS payed_card).
 * Mijozning karta to'lovi (cardCollected) kompaniya hisobiga to'g'ridan-to'g'ri tushadi — kuryer qo'lida emas.
 * Yetkazilmagan topshiriqlar summaga kirmaydi; ular kuryerdan qabul qilingan (checked) bo'lishi shart.
 *
 * **`checked` QOIDASI** (DMS 0049, `mode`):
 *   rasmiy (`official`) — yetkazilgan topshiriq hisobga `(NOT «narx o'zgardi») OR checked` bo'lsa
 *   kiradi, refund esa FAQAT `checked` bo'lsa; taxmin (`estimate`) — ikkala shart ham yo'q, refund
 *   yetkazilgan bo'lsa ham kiradi. Refund (`kind='refund'`) topshirig'i hech qachon yig'ilgan pul va
 *   ish haqiga KIRMAYDI — u faqat `refunds` ga (kuryer mijozga qaytargan pul) tushadi.
 */
export function calcSettlement(
  tasks: readonly SettlementTask[],
  rate: CourierRate | null,
  paid: SettlementInput,
  opts: { mode?: SettlementMode } = {},
): SettlementSummary {
  const r: CourierRate = rate ?? { cashRate: 0, cardRate: 0, rules: [] };
  const mode: SettlementMode = opts.mode ?? 'official';
  const s: SettlementSummary = {
    mode,
    totalTasks: tasks.length,
    deliveredCount: 0,
    partialCount: 0,
    returnedCount: 0,
    mustReturnCount: 0,
    deliveredAmount: 0,
    cashCollected: 0,
    cardCollected: 0,
    refunds: 0,
    salaryCash: 0,
    salaryCard: 0,
    salary: 0,
    expectedCash: 0,
    paidCash: round2(paid.paidCash),
    paidCard: round2(paid.paidCard),
    remained: 0,
    byRule: [],
    pendingDelivered: 0,
    pendingRefund: 0,
    pendingAmount: 0,
  };
  const rules = new Map<string, { code: string; label: string; count: number; amount: number }>();
  for (const t of tasks) {
    const delivered = isDeliveredStatus(t.status);
    const isRefund = t.kind === 'refund';
    /*
     * Refund puli (DMS `REFUND_OFFICIAL` ⇄ `REFUND_ANY`): RASMIY hisobga faqat kuryerdan QABUL
     * QILINGACH kiradi. TAXMINDA esa pul kuryer qo'lidan chiqqan bo'lsa yetarli: `RF-` topshirig'ida
     * bu «yetkazildi», oddiy topshiriqda esa summa natija belgilanganda yoziladi — ya'ni allaqachon.
     */
    if (t.refundAmount) {
      const handedOver = isRefund ? delivered || t.checked : true;
      if (t.checked || (mode === 'estimate' && handedOver)) s.refunds += t.refundAmount;
      else s.pendingRefund++;
    }
    if (isRefund) {
      // RF- topshirig'i yig'ilgan pulga ham, ish haqiga ham kirmaydi (DMS: order_type='order' sharti).
      if (!delivered && !t.checked) s.mustReturnCount++;
      else if (!delivered) s.returnedCount++;
      continue;
    }
    if (delivered) {
      // DMS `assign_view`: qabul qilinmagan «Narx o'zgardi» RASMIY hisobga kirmaydi.
      if (mode === 'official' && t.status === 'partial' && !t.checked) {
        s.pendingDelivered++;
        s.pendingAmount += t.cashAmount + t.cardAmount;
        continue;
      }
      if (t.status === 'partial') s.partialCount++;
      else s.deliveredCount++;
      s.cashCollected += t.cashAmount;
      s.cardCollected += t.cardAmount;
      const sal = taskSalary(r, t);
      s.salaryCash += sal.cash;
      s.salaryCard += sal.card;
      const key = sal.rule?.code ?? 'base';
      const row = rules.get(key) ?? {
        code: key,
        label: sal.rule?.label ?? 'Bazaviy stavka',
        count: 0,
        amount: 0,
      };
      row.count++;
      row.amount += sal.total;
      rules.set(key, row);
    } else if (t.checked) s.returnedCount++;
    else s.mustReturnCount++;
  }
  s.pendingAmount = round2(s.pendingAmount);
  s.cashCollected = round2(s.cashCollected);
  s.cardCollected = round2(s.cardCollected);
  s.deliveredAmount = round2(s.cashCollected + s.cardCollected);
  s.refunds = round2(s.refunds);
  s.salaryCash = round2(s.salaryCash);
  s.salaryCard = round2(s.salaryCard);
  s.salary = round2(s.salaryCash + s.salaryCard);
  s.expectedCash = round2(s.cashCollected - s.refunds - s.salaryCash);
  s.remained = round2(s.expectedCash - s.paidCash - s.paidCard);
  s.byRule = [...rules.values()].map((x) => ({ ...x, amount: round2(x.amount) }));
  return s;
}

/** Yetkazuvchanlik foizi (0..100, 1 kasr). */
export const deliverabilityPct = (delivered: number, total: number): number =>
  total > 0 ? Math.round((delivered / total) * 1000) / 10 : 0;
